import React, { useEffect } from 'react';
import { useRouteMatch } from 'react-router-dom';
import { NavigationBar, NavigationItem } from '.';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../slices/rootReducer';
import { selectors as accountListSelectors } from '../../../slices/accounts/list';
import { selectors as authSelectors } from '../../../slices/auth';
import { AccessTypes } from '../domain/accountAccessType';
import { ListParams } from '../../../slices/utils';
import { FilterOperators, Ifilter } from '../filters/interfaces';
import { fetchAccountsList } from '../../../slices/accounts/list.thunks';
import { fetchCreditsPools } from '../../../slices/credits/creditsPools/creditsPools.thunk';
import {
  selectActiveCreditsPoolsIdsCount
} from '../../../slices/credits/creditsPools/selectors';
import { isExternalClient, isNetquestClient } from '../../../utils/clients';
import { isRepDataAccount } from '../../../utils/utils';

const BILLING_ROUTE = {
  label: 'Billing',
  route: 'billing'
};

const SUBSCRIPTION_ROUTE = 'subscription';
export const CREDITS_PATH = 'credits';

const PLAN_AND_BILLING_ROUTE = {
  label: 'Plan & Billing',
  route: 'plan-billing'
};

const PROFILE_ROUTE = {
  label: 'Profile',
  route: 'profile'
};

const USERS_ROUTE = {
  label: 'Users',
  route: 'users'
};

const CREDITS_ROUTE = {
  label: 'Credits',
  route: CREDITS_PATH
};

const userNavigationRoutes = [
  PROFILE_ROUTE,
  {
    label: 'Subscription',
    route: SUBSCRIPTION_ROUTE
  }
];

const navigationRoutes = [
  ...userNavigationRoutes,
  BILLING_ROUTE,
  USERS_ROUTE
];

export const SettingsNavigationBar: React.FC = () => {
  const match = useRouteMatch();
  const user = useSelector((state: RootState) => state.auth.user);
  const url = match.url.split('/');
  url.pop();
  // Refers to a path such as /settings/subscription/edit which needs to remove an extra segment of the path, so the base can be /settings
  const isNestedPath = url.length === 3;
  if (isNestedPath) {
    url.pop();
  }
  const dispatch = useDispatch();
  const account = useSelector((state: RootState) => accountListSelectors.getAccountById(state, user?.accountId ?? ''));
  const creditPoolCount = useSelector(selectActiveCreditsPoolsIdsCount);
  const isCreditPoolUserFacing = useSelector(authSelectors.isCreditPoolUserFacing);

  const shouldAppendCreditPoolRoute = () => {
    const hasCreditsRoute = navigationRoutes.some((route) => route.route === CREDITS_PATH);
    const isValidClient = !isExternalClient() || isNetquestClient();
    return isValidClient && isCreditPoolUserFacing && !hasCreditsRoute && creditPoolCount > 0;
  };

  useEffect(() => {
    if (!account) {
      const fetchAccountsRequest: ListParams<Ifilter[]> = {
        filters: [
          {
            field: '_id',
            operands: [user?.accountId],
            operator: FilterOperators.isAnyOf,
            validateInputs: false
          }
        ],
        pagination: { offset: 0, limit: 25 }
      };
      dispatch(fetchAccountsList(fetchAccountsRequest));
      if (user?.accountId && isCreditPoolUserFacing) {
        dispatch(fetchCreditsPools(user.accountId));
      }
    }
  }, [account, dispatch, isCreditPoolUserFacing, user?.accountId]);
  const isUserAdmin = useSelector(authSelectors.isAuthUserAdminOrOwner);
  const isUserSxAdmin = useSelector(authSelectors.isAuthUserSightxAdmin);
  if (!account) {
    return null;
  }
  const isEnterpriseAccount = account.accessType === AccessTypes.Enterprise;
  const isLiteAccount = account.accessType === AccessTypes.Lite;
  const tabsToHideWhenEnterprise = [BILLING_ROUTE.label];
  const netquestHiddenTabs = [BILLING_ROUTE, SUBSCRIPTION_ROUTE];
  const baseURL = url.join('/');

  if (isEnterpriseAccount && !isUserSxAdmin) {
    let currentRoutes = [PROFILE_ROUTE, PLAN_AND_BILLING_ROUTE, USERS_ROUTE];
    if (isExternalClient()) {
      currentRoutes = currentRoutes.filter((route) => PLAN_AND_BILLING_ROUTE !== route);
    }
    if (isRepDataAccount(account.id)) {
      currentRoutes = currentRoutes.filter((route) => PLAN_AND_BILLING_ROUTE !== route && USERS_ROUTE !== route);
    }

    if (shouldAppendCreditPoolRoute()) {
      currentRoutes.push(CREDITS_ROUTE);
    }

    const navItems = currentRoutes.map((item) => ({
      label: item.label,
      path: `${baseURL}/${item.route}`,
      key: item.route,
      disabled: false
    }));
    return (
      <NavigationBar navItems={navItems} getCurrentPath={(url) => url[2] || ''} />
    );
  }

  if (isEnterpriseAccount && !isUserAdmin && !isUserSxAdmin) {
    const enabledRoutes = [PROFILE_ROUTE, USERS_ROUTE];
    const navItems = enabledRoutes.map((item) => ({
      label: item.label,
      path: `${baseURL}/${item.route}`,
      key: item.route,
      disabled: false
    }));

    return (
      <NavigationBar navItems={navItems} getCurrentPath={(url) => url[2] || ''} />
    );
  }

  if (shouldAppendCreditPoolRoute()) {
    navigationRoutes.push(CREDITS_ROUTE);
  }

  const disableBilling = !isUserAdmin && !isUserSxAdmin;
  const navItems: NavigationItem[] = navigationRoutes.reduce((items: NavigationItem[], item) => {
    const shouldHideForEnterprise = tabsToHideWhenEnterprise.includes(item.label) && isEnterpriseAccount;
    const shouldHideForExternal = netquestHiddenTabs.includes(item.route) && isExternalClient();
    if ((item.label === BILLING_ROUTE.label && isLiteAccount) || shouldHideForEnterprise || shouldHideForExternal) {
      return items;
    }

    items.push({
      label: item.label,
      path: `${baseURL}/${item.route}`,
      key: item.route,
      disabled: item.label === BILLING_ROUTE.label ? disableBilling : false
    });
    return items;
  }, []);

  return (
    <NavigationBar navItems={navItems} getCurrentPath={(url) => url[2] || ''} />
  );
};
